import { fetchLedgerBalance, fetchLedgerEntries, fetchLedgerSummary } from '../../util/api';
import { fetchCurrentUser } from '../../ducks/user.duck';

// ================ Action types ================ //

export const FETCH_WALLET_BALANCE_REQUEST = 'app/MerchantWalletPage/FETCH_WALLET_BALANCE_REQUEST';
export const FETCH_WALLET_BALANCE_SUCCESS = 'app/MerchantWalletPage/FETCH_WALLET_BALANCE_SUCCESS';
export const FETCH_WALLET_BALANCE_ERROR = 'app/MerchantWalletPage/FETCH_WALLET_BALANCE_ERROR';

export const FETCH_WALLET_ENTRIES_REQUEST = 'app/MerchantWalletPage/FETCH_WALLET_ENTRIES_REQUEST';
export const FETCH_WALLET_ENTRIES_SUCCESS = 'app/MerchantWalletPage/FETCH_WALLET_ENTRIES_SUCCESS';
export const FETCH_WALLET_ENTRIES_ERROR = 'app/MerchantWalletPage/FETCH_WALLET_ENTRIES_ERROR';

export const FETCH_WALLET_SUMMARY_REQUEST = 'app/MerchantWalletPage/FETCH_WALLET_SUMMARY_REQUEST';
export const FETCH_WALLET_SUMMARY_SUCCESS = 'app/MerchantWalletPage/FETCH_WALLET_SUMMARY_SUCCESS';
export const FETCH_WALLET_SUMMARY_ERROR = 'app/MerchantWalletPage/FETCH_WALLET_SUMMARY_ERROR';

// Manual payout request action types removed - payouts are now automated
export const CLEAR_PAYOUT_STATE = 'app/MerchantWalletPage/CLEAR_PAYOUT_STATE';


// ================ Reducer ================ //

const initialState = {
  // Balance
  balance: null,
  balanceInProgress: false,
  balanceError: null,
  
  // Entries
  entries: [],
  entriesInProgress: false,
  entriesError: null,
  pagination: null,
  
  // Summary
  summary: null,
  summaryInProgress: false,
  summaryError: null,
  
  // Payout actions
  payoutLoading: false,
  payoutSuccess: null,
  payoutError: null,
  
};

export default function merchantWalletPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    // Balance
    case FETCH_WALLET_BALANCE_REQUEST:
      return {
        ...state,
        balanceInProgress: true,
        balanceError: null,
      };
    case FETCH_WALLET_BALANCE_SUCCESS:
      return {
        ...state,
        balance: payload,
        balanceInProgress: false,
      };
    case FETCH_WALLET_BALANCE_ERROR:
      return {
        ...state,
        balanceInProgress: false,
        balanceError: payload,
      };

    // Entries
    case FETCH_WALLET_ENTRIES_REQUEST:
      return {
        ...state,
        entriesInProgress: true,
        entriesError: null,
      };
    case FETCH_WALLET_ENTRIES_SUCCESS:
      return {
        ...state,
        entries: payload.entries || [],
        pagination: payload.pagination || null,
        entriesInProgress: false,
      };
    case FETCH_WALLET_ENTRIES_ERROR:
      return {
        ...state,
        entriesInProgress: false,
        entriesError: payload,
      };

    // Summary
    case FETCH_WALLET_SUMMARY_REQUEST:
      return {
        ...state,
        summaryInProgress: true,
        summaryError: null,
      };
    case FETCH_WALLET_SUMMARY_SUCCESS:
      return {
        ...state,
        summary: payload,
        summaryInProgress: false,
      };
    case FETCH_WALLET_SUMMARY_ERROR:
      return {
        ...state,
        summaryInProgress: false,
        summaryError: payload,
      };

    // Manual payout actions removed - payouts are now automated
    case CLEAR_PAYOUT_STATE:
      return {
        ...state,
        payoutLoading: false,
        payoutSuccess: null,
        payoutError: null,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

// Fetch wallet balance
export const fetchWalletBalanceRequest = () => ({ type: FETCH_WALLET_BALANCE_REQUEST });
export const fetchWalletBalanceSuccess = balance => ({
  type: FETCH_WALLET_BALANCE_SUCCESS,
  payload: balance,
});
export const fetchWalletBalanceError = error => ({
  type: FETCH_WALLET_BALANCE_ERROR,
  payload: error,
  error: true,
});

// Fetch wallet entries
export const fetchWalletEntriesRequest = () => ({ type: FETCH_WALLET_ENTRIES_REQUEST });
export const fetchWalletEntriesSuccess = data => ({
  type: FETCH_WALLET_ENTRIES_SUCCESS,
  payload: data,
});
export const fetchWalletEntriesError = error => ({
  type: FETCH_WALLET_ENTRIES_ERROR,
  payload: error,
  error: true,
});

// Fetch wallet summary
export const fetchWalletSummaryRequest = () => ({ type: FETCH_WALLET_SUMMARY_REQUEST });
export const fetchWalletSummarySuccess = summary => ({
  type: FETCH_WALLET_SUMMARY_SUCCESS,
  payload: summary,
});
export const fetchWalletSummaryError = error => ({
  type: FETCH_WALLET_SUMMARY_ERROR,
  payload: error,
  error: true,
});

// Manual payout request actions removed - payouts are now automated via cron jobs
export const clearPayoutState = () => ({ type: CLEAR_PAYOUT_STATE });


// ================ Thunks ================ //

export const loadWalletBalance = merchantId => async (dispatch) => {
  dispatch(fetchWalletBalanceRequest());
  
  try {
    const response = await fetchLedgerBalance(merchantId);
    dispatch(fetchWalletBalanceSuccess(response));
    return response;
  } catch (error) {
    dispatch(fetchWalletBalanceError(error));
    throw error;
  }
};

export const loadWalletEntries = (merchantId, options = {}) => async (dispatch) => {
  dispatch(fetchWalletEntriesRequest());
  
  try {
    const response = await fetchLedgerEntries(merchantId, options);
    dispatch(fetchWalletEntriesSuccess(response));
    return response;
  } catch (error) {
    dispatch(fetchWalletEntriesError(error));
    throw error;
  }
};

export const loadWalletSummary = (merchantId, startDate, endDate) => async (dispatch) => {
  dispatch(fetchWalletSummaryRequest());
  
  try {
    const response = await fetchLedgerSummary(merchantId, startDate, endDate);
    dispatch(fetchWalletSummarySuccess(response));
    return response;
  } catch (error) {
    dispatch(fetchWalletSummaryError(error));
    throw error;
  }
};

// Load all wallet data
export const loadWalletData = merchantId => async (dispatch) => {
  try {
    await Promise.all([
      dispatch(loadWalletBalance(merchantId)),
      dispatch(loadWalletEntries(merchantId, { limit: 20 })),
    ]);
  } catch (error) {
    // Log error details for debugging (production logging)
    if (process.env.NODE_ENV !== 'production') {
      console.error('[MerchantWalletPage] Error loading wallet data:', error);
    }
    throw error;
  }
};

// Manual payout request function removed - payouts are now automated via cron jobs


// Server-side data loading
export const loadData = (params, search) => async (dispatch, getState) => {
  // Fetch current user first (in case of direct navigation, user might not be loaded yet)
  await dispatch(fetchCurrentUser());
  
  const state = getState();
  const { currentUser } = state.user;
  
  if (currentUser && currentUser.id) {
    const merchantId = currentUser.id.uuid;
    return dispatch(loadWalletData(merchantId));
  }
  
  return Promise.resolve();
};
